/**
 * Analytics Panel Component
 * Displays analytics results in table format below the map
 */

import React, { useState, useEffect } from 'react';
import { BarChart3, Users, TrendingUp, MapPin, FileText, DollarSign, Activity, Droplets } from 'lucide-react';

function AnalyticsPanel({ analyticsData, onFilterClick }) {
  const [revenueData, setRevenueData] = useState(null);
  console.log('AnalyticsPanel render. analyticsData:', analyticsData);

  // Handle row click to trigger filter
  const handleRowClick = (filterType, value) => {
    if (onFilterClick) {
      console.log('📊 Analytics filter clicked:', filterType, value);
      onFilterClick(filterType, value);
    }
  };

  // Extract counties from GIS query results for context-aware filtering
  const getCountiesFromResults = () => {
    if (!analyticsData?.summary?.byField?.county) return null;
    const counties = Object.keys(analyticsData.summary.byField.county);
    return counties.length > 0 ? counties : null;
  };

  // Fetch revenue totals when panel is shown - filtered by GIS results context
  useEffect(() => {
    async function fetchRevenue() {
      try {
        const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5001';

        // Build URL with county filter if available from GIS results
        const counties = getCountiesFromResults();
        const url = counties
          ? `${backendUrl}/api/data/revenue/totals?counties=${encodeURIComponent(counties.join(','))}`
          : `${backendUrl}/api/data/revenue/totals`;

        console.log('📊 Fetching revenue data with filter:', counties);

        const response = await fetch(url);
        if (response.ok) {
          const data = await response.json();
          setRevenueData(data);
        }
      } catch (error) {
        console.log('Revenue data not available:', error.message);
      }
    }
    if (analyticsData) {
      fetchRevenue();
    }
  }, [analyticsData]);

  if (!analyticsData) {
    console.log('AnalyticsPanel: No data, returning null');
    return null;
  }

  const { summary, query } = analyticsData;
  console.log('AnalyticsPanel: Rendering with query:', query, 'summary:', summary);

  // Parse the summary data
  const totals = {
    count: summary.totalCount || 0,
    acreage: summary.totalAcreage || 0,
    royaltyAcres: summary.totalRoyaltyAcres || 0,
    nri: summary.totalNRI || 0,
    orri: summary.totalORRI || 0
  };

  // Detect data type (tracts vs wells)
  const dataType = summary.dataType || 'tracts';

  // Convert grouped data to arrays for tables
  const operatorData = summary.byField?.operator
    ? Object.entries(summary.byField.operator).map(([name, count]) => ({ name, count }))
    : [];

  const countyData = summary.byField?.county
    ? Object.entries(summary.byField.county).map(([name, count]) => ({ name, count }))
    : [];

  const statusData = summary.byField?.leaseStatus
    ? Object.entries(summary.byField.leaseStatus).map(([name, count]) => ({ name, count }))
    : [];

  // Formation data (for wells)
  const formationData = summary.byField?.formation
    ? Object.entries(summary.byField.formation).map(([name, count]) => ({ name, count }))
    : [];

  // Contract data
  const contractStats = summary.contracts;
  const hasContractData = contractStats && contractStats.totalContracts > 0;

  // Contract tables
  const contractOperatorData = hasContractData && contractStats.contractsByOperator
    ? Object.entries(contractStats.contractsByOperator).map(([name, count]) => ({
        name,
        count,
        revenue: contractStats.revenueByOperator[name] || 0
      }))
    : [];

  const contractTypeData = hasContractData && contractStats.contractsByType
    ? Object.entries(contractStats.contractsByType).map(([name, count]) => ({ name, count }))
    : [];

  return (
    <div className="analytics-panel">
      <div className="analytics-header">
        <BarChart3 size={20} />
        <h3>Analytics Results</h3>
        <span className="analytics-query">{query}</span>
      </div>

      <div className="analytics-content">
        {/* GIS Summary Cards */}
        <div className="analytics-cards">
          <div className="analytics-card">
            <MapPin size={24} className="card-icon" />
            <div className="card-content">
              <div className="card-value">{totals.count.toLocaleString()}</div>
              <div className="card-label">{dataType === 'wells' ? 'Total Wells' : 'Total Tracts'}</div>
            </div>
          </div>

          {totals.acreage > 0 && (
            <div className="analytics-card">
              <TrendingUp size={24} className="card-icon" />
              <div className="card-content">
                <div className="card-value">{Math.round(totals.acreage).toLocaleString()}</div>
                <div className="card-label">Total Acres</div>
              </div>
            </div>
          )}

          {totals.royaltyAcres > 0 && (
            <div className="analytics-card">
              <BarChart3 size={24} className="card-icon" />
              <div className="card-content">
                <div className="card-value">{Math.round(totals.royaltyAcres).toLocaleString()}</div>
                <div className="card-label">Net Mineral Acres</div>
              </div>
            </div>
          )}

          {totals.nri > 0 && (
            <div className="analytics-card">
              <DollarSign size={24} className="card-icon" />
              <div className="card-content">
                <div className="card-value">{totals.nri.toFixed(4)}</div>
                <div className="card-label">Total NRI</div>
              </div>
            </div>
          )}

          {totals.orri > 0 && (
            <div className="analytics-card">
              <Activity size={24} className="card-icon" />
              <div className="card-content">
                <div className="card-value">{(totals.orri * 100).toFixed(2)}%</div>
                <div className="card-label">Total ORRI</div>
              </div>
            </div>
          )}
        </div>

        {/* Contract Summary Cards */}
        {hasContractData && (
          <>
            <div className="analytics-section-header">
              <FileText size={20} />
              <h4>Contract Analytics</h4>
            </div>

            <div className="analytics-cards">
              <div className="analytics-card contract-card">
                <FileText size={24} className="card-icon" />
                <div className="card-content">
                  <div className="card-value">{contractStats.totalContracts}</div>
                  <div className="card-label">Active Contracts</div>
                </div>
              </div>

              <div className="analytics-card contract-card">
                <Activity size={24} className="card-icon" />
                <div className="card-content">
                  <div className="card-value">{contractStats.activeContracts}</div>
                  <div className="card-label">Active Leases</div>
                </div>
              </div>

              {contractStats.totalMonthlyRevenue > 0 && (
                <div className="analytics-card contract-card">
                  <DollarSign size={24} className="card-icon" />
                  <div className="card-content">
                    <div className="card-value">
                      ${(contractStats.totalMonthlyRevenue / 1000).toFixed(0)}K
                    </div>
                    <div className="card-label">Monthly Revenue</div>
                  </div>
                </div>
              )}

              <div className="analytics-card contract-card">
                <TrendingUp size={24} className="card-icon" />
                <div className="card-content">
                  <div className="card-value">
                    {(contractStats.averageRoyaltyPercentage * 100).toFixed(1)}%
                  </div>
                  <div className="card-label">Avg Royalty %</div>
                </div>
              </div>
            </div>
          </>
        )}

        {/* GIS Tables */}
        <div className="analytics-tables">
          {operatorData.length > 0 && (
            <div className="analytics-table-container">
              <h4><Users size={16} /> By Operator (GIS)</h4>
              <table className="analytics-table clickable-table">
                <thead>
                  <tr>
                    <th>Operator</th>
                    <th>Tracts</th>
                    <th>%</th>
                  </tr>
                </thead>
                <tbody>
                  {operatorData
                    .sort((a, b) => b.count - a.count)
                    .map((item, idx) => (
                      <tr
                        key={idx}
                        className="clickable-row"
                        onClick={() => handleRowClick('operator', item.name)}
                        title={`Click to filter by ${item.name}`}
                      >
                        <td>{item.name}</td>
                        <td className="count-cell">{item.count}</td>
                        <td className="percent-cell">
                          {((item.count / totals.count) * 100).toFixed(1)}%
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          )}

          {countyData.length > 0 && (
            <div className="analytics-table-container">
              <h4><MapPin size={16} /> By County</h4>
              <table className="analytics-table clickable-table">
                <thead>
                  <tr>
                    <th>County</th>
                    <th>Tracts</th>
                    <th>%</th>
                  </tr>
                </thead>
                <tbody>
                  {countyData
                    .sort((a, b) => b.count - a.count)
                    .map((item, idx) => (
                      <tr
                        key={idx}
                        className="clickable-row"
                        onClick={() => handleRowClick('county', item.name)}
                        title={`Click to filter by ${item.name}`}
                      >
                        <td>{item.name}</td>
                        <td className="count-cell">{item.count}</td>
                        <td className="percent-cell">
                          {((item.count / totals.count) * 100).toFixed(1)}%
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          )}

          {statusData.length > 0 && (
            <div className="analytics-table-container">
              <h4><BarChart3 size={16} /> By {dataType === 'wells' ? 'Well Status' : 'Lease Status'}</h4>
              <table className="analytics-table clickable-table">
                <thead>
                  <tr>
                    <th>Status</th>
                    <th>{dataType === 'wells' ? 'Wells' : 'Tracts'}</th>
                    <th>%</th>
                  </tr>
                </thead>
                <tbody>
                  {statusData
                    .sort((a, b) => b.count - a.count)
                    .slice(0, 10)
                    .map((item, idx) => (
                      <tr
                        key={idx}
                        className="clickable-row"
                        onClick={() => handleRowClick('status', item.name)}
                        title={`Click to filter by ${item.name}`}
                      >
                        <td>{item.name}</td>
                        <td className="count-cell">{item.count}</td>
                        <td className="percent-cell">
                          {((item.count / totals.count) * 100).toFixed(1)}%
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          )}

          {formationData.length > 0 && (
            <div className="analytics-table-container">
              <h4><Activity size={16} /> By Formation</h4>
              <table className="analytics-table clickable-table">
                <thead>
                  <tr>
                    <th>Formation</th>
                    <th>Wells</th>
                    <th>%</th>
                  </tr>
                </thead>
                <tbody>
                  {formationData
                    .sort((a, b) => b.count - a.count)
                    .slice(0, 10)
                    .map((item, idx) => (
                      <tr
                        key={idx}
                        className="clickable-row"
                        onClick={() => handleRowClick('formation', item.name)}
                        title={`Click to filter by ${item.name}`}
                      >
                        <td>{item.name}</td>
                        <td className="count-cell">{item.count}</td>
                        <td className="percent-cell">
                          {((item.count / totals.count) * 100).toFixed(1)}%
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Contract Tables */}
        {hasContractData && (
          <div className="analytics-tables">
            {contractOperatorData.length > 0 && (
              <div className="analytics-table-container">
                <h4><Users size={16} /> Contract Operators</h4>
                <table className="analytics-table">
                  <thead>
                    <tr>
                      <th>Operator</th>
                      <th>Contracts</th>
                      <th>Monthly Revenue</th>
                    </tr>
                  </thead>
                  <tbody>
                    {contractOperatorData
                      .sort((a, b) => b.revenue - a.revenue)
                      .map((item, idx) => (
                        <tr key={idx}>
                          <td>{item.name}</td>
                          <td className="count-cell">{item.count}</td>
                          <td className="revenue-cell">
                            ${item.revenue.toLocaleString()}
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            )}

            {contractTypeData.length > 0 && (
              <div className="analytics-table-container">
                <h4><FileText size={16} /> Contract Types</h4>
                <table className="analytics-table">
                  <thead>
                    <tr>
                      <th>Type</th>
                      <th>Contracts</th>
                      <th>%</th>
                    </tr>
                  </thead>
                  <tbody>
                    {contractTypeData
                      .sort((a, b) => b.count - a.count)
                      .map((item, idx) => (
                        <tr key={idx}>
                          <td>{item.name}</td>
                          <td className="count-cell">{item.count}</td>
                          <td className="percent-cell">
                            {((item.count / contractStats.totalContracts) * 100).toFixed(1)}%
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Revenue Data Section */}
        {revenueData && revenueData.totals && (
          <>
            <div className="analytics-section-header revenue-header">
              <DollarSign size={20} />
              <h4>Production Revenue Analytics</h4>
            </div>

            <div className="analytics-cards">
              <div className="analytics-card revenue-card">
                <DollarSign size={24} className="card-icon" />
                <div className="card-content">
                  <div className="card-value">
                    ${(revenueData.totals.total_revenue / 1000000).toFixed(1)}M
                  </div>
                  <div className="card-label">Total Revenue</div>
                </div>
              </div>

              <div className="analytics-card revenue-card">
                <BarChart3 size={24} className="card-icon" />
                <div className="card-content">
                  <div className="card-value">
                    {(revenueData.totals.total_records / 1000000).toFixed(1)}M
                  </div>
                  <div className="card-label">Revenue Records</div>
                </div>
              </div>

              <div className="analytics-card revenue-card">
                <Activity size={24} className="card-icon" />
                <div className="card-content">
                  <div className="card-value">
                    {revenueData.totals.unique_dsus?.toLocaleString() || 0}
                  </div>
                  <div className="card-label">Unique DSUs</div>
                </div>
              </div>
            </div>

            {/* Revenue by Product Table */}
            <div className="analytics-tables">
              {revenueData.byProduct && revenueData.byProduct.length > 0 && (
                <div className="analytics-table-container">
                  <h4><Droplets size={16} /> Revenue by Product</h4>
                  <table className="analytics-table">
                    <thead>
                      <tr>
                        <th>Product</th>
                        <th>Revenue</th>
                        <th>%</th>
                      </tr>
                    </thead>
                    <tbody>
                      {revenueData.byProduct.map((item, idx) => (
                        <tr key={idx}>
                          <td style={{ textTransform: 'capitalize' }}>{item.product}</td>
                          <td className="revenue-cell">
                            ${(item.revenue / 1000000).toFixed(1)}M
                          </td>
                          <td className="percent-cell">
                            {((item.revenue / revenueData.totals.total_revenue) * 100).toFixed(1)}%
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {revenueData.byCounty && revenueData.byCounty.length > 0 && (
                <div className="analytics-table-container">
                  <h4><MapPin size={16} /> Revenue by County</h4>
                  <table className="analytics-table">
                    <thead>
                      <tr>
                        <th>County</th>
                        <th>Revenue</th>
                        <th>Records</th>
                      </tr>
                    </thead>
                    <tbody>
                      {revenueData.byCounty.slice(0, 8).map((item, idx) => (
                        <tr key={idx}>
                          <td>{item.county}</td>
                          <td className="revenue-cell">
                            ${(item.revenue / 1000000).toFixed(1)}M
                          </td>
                          <td className="count-cell">
                            {(item.records / 1000).toFixed(0)}K
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default AnalyticsPanel;
